const API_BASE = process.env.REACT_APP_API_URL || '';
const API_HOSTED_PATH_PREFIXES = ['/api/', '/uploads/'];

export const BRAND_LOGO_SCOPE_OPTIONS = [
  { value: 'landing_home_header', label: '首页主导航', description: '官网首页顶部导航与品牌入口' },
  { value: 'landing_home_footer', label: '首页底部', description: '官网首页底部品牌区与版权区' },
  { value: 'user_workspace_brand', label: '用户后台品牌', description: '用户系统左侧菜单展开态品牌 Logo' },
  { value: 'user_workspace_icon', label: '用户后台图标', description: '用户系统左侧菜单收起态图标 Logo' },
  { value: 'user_login', label: '用户登录/注册页', description: '用户登录页与注册页品牌展示' },
  { value: 'admin_workspace_brand', label: '管理员后台品牌', description: '管理员系统左侧菜单展开态品牌 Logo' },
  { value: 'admin_workspace_icon', label: '管理员后台图标', description: '管理员系统左侧菜单收起态图标 Logo' },
  { value: 'admin_login', label: '管理员登录页', description: '管理员登录页品牌展示' },
  { value: 'report', label: '报告页', description: '报告页页眉与导出报告品牌 Logo' },
] as const;

export type BrandLogoScope = typeof BRAND_LOGO_SCOPE_OPTIONS[number]['value'];

export interface BrandLogoAsset {
  id: string;
  name: string;
  url: string;
  scopes: BrandLogoScope[];
  note?: string;
}

const BRAND_LOGO_SCOPE_SET = new Set<string>(BRAND_LOGO_SCOPE_OPTIONS.map((item) => item.value));

export const unwrapSiteConfigValue = <T = any>(value: any): T => {
  if (value && typeof value === 'object' && '_value' in value) {
    return value._value as T;
  }
  return value as T;
};

export const resolvePublicAssetUrl = (url?: string | null): string => {
  if (!url) {
    return '';
  }
  if (/^(https?:)?\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  const normalized = url.startsWith('/') ? url : `/${url}`;
  if (!API_BASE) {
    return normalized;
  }
  const shouldUseApiBase = API_HOSTED_PATH_PREFIXES.some((prefix) => normalized.startsWith(prefix));
  if (!shouldUseApiBase) {
    return normalized;
  }
  return `${API_BASE.replace(/\/+$/, '')}${normalized}`;
};

export const isExternalHref = (href?: string | null): boolean => {
  if (!href) {
    return false;
  }
  return /^(https?:)?\/\//i.test(href);
};

export const normalizeBrandLogoAssets = (value: any): BrandLogoAsset[] => {
  const unwrapped = unwrapSiteConfigValue<any>(value);
  const source: unknown[] = Array.isArray(unwrapped)
    ? unwrapped
    : Array.isArray(unwrapped?.assets)
      ? unwrapped.assets
      : Array.isArray(unwrapped?.items)
        ? unwrapped.items
        : [];

  return source.reduce((acc: BrandLogoAsset[], item: unknown, index: number) => {
    if (!item || typeof item !== 'object') {
      return acc;
    }

    const record = item as Record<string, unknown>;
    const id = typeof record.id === 'string' && record.id.trim() ? record.id.trim() : `logo-${index + 1}`;
    const name = typeof record.name === 'string' ? record.name.trim() : '';
    const url = typeof record.url === 'string' ? record.url.trim() : '';
    const note = typeof record.note === 'string' && record.note.trim() ? record.note.trim() : undefined;
    const scopes = Array.isArray(record.scopes)
      ? Array.from(
        new Set(
          record.scopes
            .map((scope) => (typeof scope === 'string' ? scope.trim() : ''))
            .filter((scope): scope is BrandLogoScope => Boolean(scope) && BRAND_LOGO_SCOPE_SET.has(scope)),
        ),
      )
      : [];

    if (!name && !url && scopes.length === 0 && !note) {
      return acc;
    }

    acc.push({
      id,
      name: name || `Logo ${index + 1}`,
      url,
      scopes,
      ...(note ? { note } : {}),
    });
    return acc;
  }, []);
};

export const resolveBrandLogoByScope = (
  value: any,
  scope: BrandLogoScope,
  fallback = '',
): string => {
  const assets = normalizeBrandLogoAssets(value);
  const matched = assets.find((item) => item.url && item.scopes.includes(scope));
  return matched?.url || fallback;
};

export const resolveScopedBrandLogoUrl = (
  value: any,
  scope: BrandLogoScope,
  configuredUrl?: string | null,
  fallback = '',
): string => {
  const normalizedConfiguredUrl = typeof configuredUrl === 'string' ? configuredUrl.trim() : '';
  const scopedUrl = resolveBrandLogoByScope(value, scope, normalizedConfiguredUrl);
  return resolvePublicAssetUrl(scopedUrl) || resolvePublicAssetUrl(normalizedConfiguredUrl) || fallback;
};

export const resolveWorkspaceBranding = (
  value: any,
  options: {
    brandScope: BrandLogoScope;
    iconScope: BrandLogoScope;
    brandLogoUrl?: string | null;
    iconLogoUrl?: string | null;
  },
): { brandUrl: string; iconUrl: string } => ({
  brandUrl: resolveScopedBrandLogoUrl(value, options.brandScope, options.brandLogoUrl),
  iconUrl: resolveScopedBrandLogoUrl(value, options.iconScope, options.iconLogoUrl),
});
